import { Layout, locale, util } from '@glu/core';
import screenManager from '@glu/screen-manager';
import PageSizeControls from './pageSizeControls';
import PaginationControls from './paginationControls';
import ViewingDetails from './viewingDetails';

import advancedTpl from './paginateAdvanced.hbs';
import intermediateTpl from './paginateIntermediate.hbs';
import simpleTpl from './paginateSimple.hbs';

export default Layout.extend({
  className() {
    return 'pager clearfix';
  },

  events: {
    'click .prev-page': 'handlePrevClick',
    'click .next-page': 'handleNextClick',
    'click .page-item': 'handlePageItemClick',
    'click .first-page': 'handleFirstPageClick',
    'click .last-page': 'handleLastPageClick',
    submit: 'handleSubmit'
  },

  regions: {
    viewingDetails: '.js-region-grid-viewing-details',
    pageSizeControls: '.js-region-grid-page-size-controls',
    paginationControls: '.js-region-grid-pagination-controls'
  },

  appEvents() {
    if (util.isNumber(this.currentPage)) {
      return {
        'grid:filter': () => {
          // If the filtered data does not have enough records to
          // populate the user's current page, reset their page view to 0
          if (
            !this.serverSidePagination &&
            this.getRecordCount() < ((this.currentPage - 1) * this.pageSize) + 1
          ) {
            this.handleFirstPageClick();
          }
        }
      };
    }

    return {};
  },

  initialize(options) {
    // need to add this because this.options is used in some of the methods.
    options = options || {};
    this.options = options;

    this.type = this.options.type || 'advanced';
    this.pageSize = this.options.pageSize || 5;
    this.currentPage = this.options.currentPage || 1;
    this.pageSizeValues = this.options.pageSizeValues || [3, 5, 10, 25, 50, 100, 250];
    this.outputCollection = this.options.outputCollection;
    this.inputCollection = this.options.inputCollection;
    this.paginateMethod = this.options.paginateMethod;
    this.stateModel = this.options.stateModel;
    this.total = this.inputCollection.length > 0 ? Math.ceil(this.inputCollection.length / this.pageSize) : 1;
    this.renderPageSizeView = util.once(this.showPageSizeControls.bind(this));

    this.saveState();

    if (typeof this.paginateMethod === 'function') {
      this.serverSidePagination = true;
    }

    this.listenTo(this.inputCollection, {
      // need to proxy this events from input collection to gridCollection.
      add: util.debounce(this.addItem.bind(this), 0),
      remove: util.debounce(this.removeItem.bind(this), 0),
      'reset sort': util.debounce(this.handleInputChange.bind(this), 0),
      sync: util.debounce(util.bind(this.updateChildViews, this), 0)
    });
  },

  addItem(model) {
    if (this.isClosed) {
      return;
    }
    this.updatePager();
    this.updateChildViews();
    this.outputCollection.add(model);
  },

  removeItem(model) {
    if (this.isClosed) {
      return;
    }
    this.updatePager();
    this.updateChildViews();
    this.outputCollection.remove(model);
  },

  updatePager() {
    if (this.isClosed) {
      return;
    }
    this.calcTotal();
    this.boundCurrentPage();
    this.buildPages();
  },

  slice() {
    const start = this.getCurrentPage() > 1 ? (this.getCurrentPage() - 1) * this.pageSize : 0;
    const end = start + (this.pageSize);
    const result = this.inputCollection.slice(start, end);

    this.updatePager();

    if (this.serverSidePagination) {
      return this.outputCollection.reset(this.inputCollection.models);
    }

    this.outputCollection.reset(result);
  },

  onRender() {
    this.updateChildViews();
    // if SM is not rendered yet it can trigger an event
    this.listenToOnce(screenManager, 'change', this.updatePagerView, this);
  },

  updateChildViews() {
    if (this.isClosed) {
      return;
    }

    this.delegateEvents();
    this.showViewingDetails();
    this.renderPageSizeView();
    this.showPaginationControls();
  },

  showPageSizeControls() {
    if (this.type !== 'advanced') {
      return;
    }

    this.pageSizeControls.show(new PageSizeControls({
      data: this.serializeData(),
      pager: this
    }));
  },

  showPaginationControls() {
    if (this.type !== 'advanced') {
      return;
    }

    this.paginationControls.show(new PaginationControls({
      data: this.serializeData(),
      pager: this
    }));
  },

  showViewingDetails() {
    if (this.type !== 'advanced') {
      return;
    }

    this.viewingDetails.show(new ViewingDetails({
      data: this.serializeData()
    }));
  },

  updatePagerView() {
    this.renderPageSizeView = util.once(this.showPageSizeControls.bind(this));
    this.render();
  },

  paginate() {
    // Broadcast a pagination event.
    this.appBus.trigger('grid:paginate', this);

    this.saveState();

    if (this.serverSidePagination) {
      // inputChange method will be called once the collection is updated
      return this.paginateMethod(this.currentPage, this.pageSize);
    }

    this.slice();
    this.updateChildViews();
  },

  getRecordCount() {
    return (typeof this.inputCollection.totalCount === 'number') ?
      this.inputCollection.totalCount :
      this.inputCollection.length;
  },

  getPageCount() {
    const totalCount = this.getRecordCount();

    // all 13 / page 5 = 2.6 but needs to be 3
    // includes handling of case where there are no records.
    return totalCount > 0 ? Math.ceil(totalCount / this.pageSize) : 1;
  },

  getCurrentPage() {
    return (typeof this.inputCollection.currentPage === 'number') ?
      this.inputCollection.currentPage :
      this.currentPage;
  },

  handleFirstPageClick() {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.paginate();
    }
  },

  handleLastPageClick() {
    this.currentPage = this.getPageCount();
    this.paginate();
  },

  handlePageItemClick(e) {
    this.currentPage = parseInt(e.target.innerHTML, 10);
    this.paginate();
  },

  handlePrevClick(e) {
    e.preventDefault();
    this.decrementCurrentPage();
    this.paginate();
  },

  handleNextClick(e) {
    e.preventDefault();
    this.incrementCurrentPage();
    this.paginate();
  },

  updatePageSizeValue(selectedValue) {
    const numericSelectedValue = +selectedValue;
    const value = util.isNaN(numericSelectedValue) ? 1000000 : numericSelectedValue;

    if (this.pageSize === value) {
      return;
    }

    this.pageSize = value;
    this.inputCollection.recordsPerPage = value;
    // reset current page to first
    this.currentPage = 1;
    this.paginate();
  },

  handlePageSelect(value) {
    if (this.currentPage === value) {
      return;
    }

    this.currentPage = value;
    this.paginate();
  },

  handleInputChange() {
    if (this.isClosed) {
      return;
    }

    this.slice();
    this.updateChildViews();
  },

  handleSubmit() {
    return false;
  },

  decrementCurrentPage() {
    this.currentPage = this.getCurrentPage() - 1;
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  },

  incrementCurrentPage() {
    this.currentPage = this.getCurrentPage() + 1;
    if (this.currentPage > this.getPageCount()) {
      this.currentPage = this.getPageCount();
    }
  },

  calcTotal() {
    // If we're using server-side pagination, there is currently no way of knowing the total
    if (this.serverSidePagination) {
      this.total = this.currentPage;
      return;
    }

    // includes handling of case where there are no records.
    this.total = this.inputCollection.length > 0 ? Math.ceil(this.inputCollection.length / this.pageSize) : 1;
  },

  boundCurrentPage() {
    if (this.currentPage >= this.total) {
      this.currentPage = this.total;
    } else if (this.currentPage < 0) {
      this.currentPage = 1;
    }
  },

  buildPages() {
    this.pages = util.range(1, this.getPageCount() + 1);
  },

  getTemplate() {
    // override template settings for small screens
    if (screenManager.isSmall() || screenManager.isExtraSmall()) {
      return simpleTpl;
    }

    switch (this.type) {
      case 'simple':
        return simpleTpl;
      case 'intermediate':
        return intermediateTpl;
      case 'advanced':
      default:
        this.type = 'advanced';
        return advancedTpl;
    }
  },

  serializeData() {
    const start = this.getCurrentPage() > 0 ? ((this.getCurrentPage() - 1) * this.pageSize) + 1 : 1;
    const totalCount = this.getRecordCount();
    const end = start + (this.pageSize - 1);

    return {
      recordRangeStart: totalCount === 0 ? 0 : start,
      recordRangeEnd: totalCount - end > 0 ? end : totalCount,
      totalRecords: totalCount,
      pageSize: this.pageSize,
      currentPage: this.getCurrentPage(),
      currentPageNumber: this.getCurrentPage(),
      pageSizeList: this.pageSizeValues,
      totalPages: this.getPageCount(),
      pages: this.pages,
      resultsName: this.options.resultsName || locale.get('records')
    };
  },

  saveState() {
    if (!this.stateModel) {
      return;
    }

    this.stateModel.set({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    });
  }
});
